import { PlayButton } from '@components/MiscComponents/VideoPlayerButton';
import dynamic from 'next/dynamic';
import { FC, useEffect, useState } from 'react';
import { ReactPlayerProps } from 'react-player';

function retryImport(fn, retriesLeft = 3, interval = 1000): Promise<any> {
  return new Promise((resolve, reject) => {
    fn()
      .then(resolve)
      .catch((error) => {
        setTimeout(() => {
          if (retriesLeft === 1) {
            window.location.reload(); // Final fallback
            return reject(error);
          }
          retryImport(fn, retriesLeft - 1, interval).then(resolve, reject);
        }, interval);
      });
  });
}
const ReactPlayer = dynamic<ReactPlayerProps>(
  () => retryImport(() => import('react-player/lazy'), 3),
  {
    ssr: false,
    loading: () => <></>,
  },
);

interface VideoProps {
  video_source?: boolean;
  youTubeUrl?: string;
  videoUpload?: string;
  posterImage?: string;
  muted?: boolean;
  loop?: boolean;
  controls?: boolean;
  playing?: boolean;
}

// This is the video that is used by the page-builder components in VideoSingle and VideoGallery
const Video: FC<VideoProps> = ({
  video_source,
  youTubeUrl,
  videoUpload,
  posterImage,
  muted,
  loop,
  controls,
  playing = true,
}) => {
  const [isPlaying, setIsPlaying] = useState(playing);
  //update is playing state when playing prop changes
  useEffect(() => {
    setIsPlaying(playing);
  }, [playing]);

  //if video source is true, then url is youTubeUrl. if false, it's videoUpload
  const url = video_source ? youTubeUrl : videoUpload;

  //Process the posterImage (cropping and resizing) before submitting to the Video component
  //check if posterImage url starts with "//images.ctfassets.net", then add "https:" to the beginning of the url
  // and then crop and resize the image
  const processedPosterImage =
    posterImage && posterImage.startsWith('//images.ctfassets.net')
      ? `https:${posterImage}?w=1280&h=720&fit=fill&fm=jpg`
      : posterImage ?? null;

  return (
    <ReactPlayer
      className="react-player"
      url={url}
      width="100%"
      height="100%"
      playing={isPlaying}
      onPlay={() => setIsPlaying(true)}
      onPause={() => setIsPlaying(false)}
      light={processedPosterImage ?? ''}
      playIcon={<PlayButton />}
      muted={muted}
      loop={loop ? true : false}
      controls={controls ? true : false}
      config={{
        vimeo: {
          playerOptions: {
            controls: controls ? true : false,
          },
        },
        youtube: {
          playerVars: { modestbranding: 1 },
        },
        file: {
          attributes: {
            controls: controls ? true : false,
          },
        },
      }}
    />
  );
};

export default Video;
